<template>
  <header :class="['c-header', { 'c-header--home': isRoot }]">
    <div class="c-header__start">
      <router-link v-if="isRoot" :to="{ name: VIEWS.home }">
        <Logo class="c-logo" />
      </router-link>

      <router-link v-else :to="{ name: VIEWS.genre, params: { slug: genreSlug } }" class="c-header__back-arrow">
        <ArrowBackIcon class="c-header__back-arrow__arrow" :alt="$t('LOGIN')" />
        <span class="c-header__back-arrow__text">{{ $t('BACK') }}</span>
      </router-link>
      <a v-if="isCM && isRoot" rel="noopener noreferrer" class="c-header__edit-btn" target="_blank" :href="generateURL">
        <EditErudioIcon />
        <span class="c-header__edit-btn__text">{{ $t('EDIT') }}</span>
      </a>
    </div>

    <div class="c-header__mid">
      <slot></slot>

      <AutoSync />

      <div v-if="showOpenedDocument" class="c-opened-document">
        <FileIcon class="c-opened-document__icon" />
        <div class="c-opened-document__name">{{ activeDocumentName }}</div>
      </div>
    </div>
    <div v-if="showHeaderEnd" class="c-header__end">
      <RouterButton
        v-if="!simplified"
        variant="simple"
        :class="['c-documents-modal-button', isRoot ? 'c-documents-modal-button--home' : '']"
        @click="openDocumentsModal"
      >
        <DocumentsIcon />
        <span>{{ $t('DOCUMENT_MODAL.TITLE') }}</span>
      </RouterButton>
      <DocumentControl v-if="isWrite" />
      <Dropdown v-if="showDropdown" :user="user" :items="dropdownItems" />
      <a v-if="showPortalButton" :href="portalLink" class="btn c-header__button-portal">
        <ProductIcon class="c-header__button-portal-icon" />
        <span class="c-header__button-portal-label">{{ portalButtonLabel }}</span>
      </a>
    </div>
  </header>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from 'vue';
import Dropdown from '@/components/dropdown/BaseDropdown.vue';
import { useRoute } from 'vue-router';
import { VIEWS } from '@/router';
import { useAuth, useTenant } from '@/core';
import ArrowBackIcon from '@/assets/icons/ArrowBackIcon.vue';
import ProductIcon from '@/assets/icons/ProductIcon.vue';
import EditErudioIcon from '@/assets/icons/EditErudioIcon.vue';
import Logo from '@/assets/icons/BaseLogo.vue';
import useModal from '@/services/useModal';
import DocumentsModal from '@/components/documents/DocumentsModal.vue';
import RouterButton from '@/components/RouterButton.vue';
import DocumentsIcon from '@/assets/icons/DocumentsIcon.vue';
import DocumentControl from '@/components/document-control/DocumentControl.vue';
import FileIcon from '@/assets/icons/FileIcon.vue';
import useExercises from '@/services/useExercises';
import AutoSync from '@/components/auto-sync/AutoSync.vue';
import { TenantConfig } from '@/types/interface/TenantConfig.interface';
import { useI18n } from 'vue-i18n';

interface HeaderProps {
  simplified?: boolean;
  genreName: string;
  genreSlug: string;
}

const props = withDefaults(defineProps<HeaderProps>(), { simplified: false });

const route = useRoute();
const { tenant } = useTenant<TenantConfig>();
const { user, userRole } = useAuth();
const { open } = useModal();
const { exercise } = useExercises();
const { t } = useI18n();
const activeDocumentName = ref<string>(null);

const rootViews = [VIEWS.home, VIEWS.genre, VIEWS.error];

const isRoot = computed(() => props.simplified || rootViews.includes(route?.name as string));
const isWrite = computed(() => route?.name === VIEWS.write && !props.simplified);
const portalLink = computed(() => tenant.value?.customElementOptions?.portalLink);
const portalButtonLabel = computed(() => tenant.value?.customElementOptions?.portalName || t('HEADER.PORTAL'));
const showPortalButton = computed(() => isRoot.value && !!portalLink.value);
const openDocumentsModal = () => {
  open(DocumentsModal);
};
const showHeaderEnd = computed(
  () => (isRoot.value && user.value) || isWrite.value || showPortalButton.value || !props.simplified,
);
const showDropdown = computed(() => isRoot.value && user.value);

const showOpenedDocument = computed(() => activeDocumentName.value && isWrite.value);

const dropdownItems = [];

const generateURL = computed(() => `${import.meta.env.VITE_ERUDIO_ADMIN_URL}/projects/${tenant.value.namespace}`);

const isCM = computed(() => userRole.value === 'content-manager');

watch(isWrite, (isWrite: boolean) => {
  if (!isWrite) {
    activeDocumentName.value = null;
  }
});
watch(exercise, () => {
  activeDocumentName.value = exercise.value.name;
});
</script>
